'use client'
/***
 *
 * @file components/TopMenu/index.tsx
 * @description TopMenu component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */

import React, { ReactNode, useEffect, useState } from "react";
// import "./TopMenu.scss";
import NavigationLogo from "../Logo";
import { getTopNavData } from "@/actions/app.action";
import { StyledNav } from "@/shared/StyledComponents";
import Modal from "@/components/Modal";
import MenuItemList from "../MenuList";
import LanguageChanger from "@/components/LanguageChanger";
import { useResponsive } from "@/client/hooks/useResponsive";
import classNames from "classnames";
import Button from "@/components/Button";
import { T_KeyValue } from "@/types/app.type";
import { Stack } from "@/layouts/LayoutComponents";
import CustomLink from "@/components/CustomLink";
import { toCamelCase, isEmptyObject } from "@/helpers/utils.helper";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { DEFAULT_LOCALE, getDefaultLocale, getLocale, getLocales } from "@/config/app.config";
import { sendEventToGTM } from "@/helpers/gtm.helper";
// import { toCamelCase } from '@/helpers/utils.helper';


/**
 * type of props to pass inside TopMenu functional component
 */
type T_Props = {
  /** id for Top menu content */
  id?: string,

  /** tags for Top menu content */
  tags?: string,

  /** isDraftEnabled/preview to enable preview mode of Top menu */
  isDraftEnabled?: boolean,

  /** data content to be displayed inside the Top menu */
  data?: any,

  /** CSS class to be added to the TopMenu component */
  className?: string,

  /** custom CSS styles to be passed in the style html attribute inside TopMenu */
  customStyles?: any,

  /** to set the alignment for menu items */
  menuAlignment?: string,

  /** locale for language selector Top menu */
  locale?: string,

  /** slug */
  slug?: string,

  /** localeList/activelanguages to show availabe languages in language selector */
  localeList?: string[],
  internalName?: string,
  backUrl?: string,
  logo?: any,
  logoAlignment?: string,
  enableBurgerMenu?: boolean,
  burgerIconPosition?: string,
  menuPosition?: string,
  pages?: any,
  ctaPositionOnTab?: string,
  ctaPositionOnMobile?: string,
  ctaBottomMargin?: number,
  ctaLeftMargin?: number,
  ctaRightMargin?: number,
  ctaAlign?: "left" | "center" | "right",
  ctaWidth?: string,
  isAMegaMenu?: boolean,
  varient?: string,
  // tabVarient?: string,
  buttonsBackgroundColor?: string,
  buttonsBorderColor?: string,
  customizeTopMenu?: boolean,
  topMenuContainer?: ReactNode | undefined,
  leftMenuSegment?: ReactNode | undefined,
  centerMenuSegment?: ReactNode | undefined,
  rightMenuSegment?: ReactNode | undefined,
  expandedMenu?: ReactNode | undefined,
  bottomStickyCTAs?: ReactNode | undefined,
  hideLeftSegment?: boolean,
  hideCenterSegment?: boolean,
  hideRightSegment?: boolean,
  sameAsTopMenuCTAs?: boolean,
  megaMenuMaxHeight?: string,
  expandEvent?: string | null,
  collapseEvent?: string | null,
  /** Event to close menu */
  closeMenuEventName?: string
}

/**
 * Name: Top Menu
 * 
 * Description: A functional component to display the Navbar throughout the application window
 * 
 * @param param0 object containing the following properties:
 *                - data: content to be displayed in the Top Menu component
 *                - className: CSS class to be applied to the TopMenu component
 *                - customStyles: CSS styles to be applied to the TopMenu component 
 * 
 * @example T_Props
 * ```
 *  type T_Props = {      
 *        data?: any,               // optional
 *        className?: string,       // optional
 *        customStyles?: any,       // optional
 *        menuAlignment?: string    // optional
 * }
 * ```
 * 
 * @returns html element navbar to be rendered on the UI
 * 
 * @remarks 
 * packages used: sendEventToGTM
 * 
 * sendEventToGTM: The sendEventToGTM function can be used to track user interactions on your page by sending events using the dataLayer object.
 * 
 * params: object containing the following properties:
 *                - event: event description
 *                - value: value to be sent to Google Tag Manager via the data layer
 * @example for sendEventToGTM
 * ```
 * <button className="btn btn-primary my-sm-0 text-upper btn-form" type="button" onClick={()=>{handleContactModalOpen(); sendEventToGTM({ event: 'Contact form click', value: `` }) }}>Contact Us</button>
 * ```
 */


const TopMenu: React.FC<T_Props> = ({
  id = "",
  tags = "",
  isDraftEnabled = false,
  data = {},
  locale = getLocale(),
  slug,
  localeList = getLocales() || [getLocale() || DEFAULT_LOCALE],
  internalName = "",
  backUrl = "",
  logo = {},
  logoAlignment = "Left",
  enableBurgerMenu = false,
  burgerIconPosition = "Left",
  menuAlignment = "Left",
  menuPosition = "",
  pages = [],
  ctaPositionOnTab = "",
  ctaPositionOnMobile = "",
  ctaBottomMargin = 15,
  ctaLeftMargin = 55,
  ctaRightMargin = 55,
  ctaAlign = "center",
  ctaWidth = "w-full",
  isAMegaMenu = false,
  varient = "",
  // tabVarient = "",
  buttonsBackgroundColor = "",
  buttonsBorderColor = "",
  customizeTopMenu = false,
  topMenuContainer,
  leftMenuSegment,
  centerMenuSegment,
  rightMenuSegment,
  expandedMenu,
  bottomStickyCTAs,
  hideLeftSegment = false,
  hideCenterSegment = false,
  hideRightSegment = false,
  sameAsTopMenuCTAs = false,
  closeMenuEventName = "",
  megaMenuMaxHeight = "max-h-[none]",
  expandEvent = null,
  collapseEvent = null
}) => {
  // const router = useRouter();
  // const searchParams = useSearchParams();
  const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top && window?.document?.getElementsByClassName("styles-module_tooltipWrapper__kqvmR")?.length > 0;
  // const isInContentfulStudio = window?.document?.getElementsByClassName("styles-module_tooltipWrapper__kqvmR");
  const preview = isDraftEnabled;
  const elData = (() => (!isEmptyObject(data) ? data : {
    menu: {
      contentType: "baseplateMenuGroup",
      content: {
        internalName,
        backUrl,
        logo: typeof (logo) === "string" ? { content: { file: { url: logo } } } : logo,
        logoAlignment,
        enableBurgerMenu,
        burgerIconPosition,
        menuAlignment,
        menuPosition,
        pages,
        ctaPositionOnTab,
        ctaPositionOnMobile,
        ctaBottomMargin,
        ctaLeftMargin,
        ctaRightMargin,
        isAMegaMenu,
        varient,
        // tabVarient,
        buttonsBackgroundColor,
        buttonsBorderColor,
      }
    }
  }))();
  const scrollDirection = useScrollDirection();

  const { isLg, isMd, isSm } = useResponsive();
  const [submits, setSubmits] = useState(0);
  const [menu, setMenu]: any = useState(elData?.menu);
  const [toggle, setToggle] = useState(false);
  const [pageScrolled, setPageScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");

  const [contactFormData, setContactFormData]: any = useState(elData?.contactFormData);
  const [downloadFormData, setDownloadFormData]: any = useState(elData?.downloadFormData);
  const [locales, setLocales] = useState<string[]>(getLocales());
  const [ctaBMargin, setCtaBMargin] = useState(`bottom-[15px]`);
  const [ctaLMargin, setCtaLMargin] = useState(`left-[auto]`);
  const [ctaRMargin, setCtaRMargin] = useState(`right-[auto]`);
  const localizedHomeHref = locale && locale !== getDefaultLocale() ? `/${locale}` : "/";

  const scrollPage = () => {
    if (window?.scrollY >= 60) {
      setPageScrolled(true);
    } else {
      setPageScrolled(false);
    }
  }

  useEffect(() => {
    setCtaBMargin(`bottom-${(() => {
      if (parseInt(ctaBottomMargin.toString(), 10) === 0) return 0;
      return `[${parseInt(ctaBottomMargin.toString(), 10)}px]`;
    })()}`)
    setCtaLMargin(`left-${(() => {
      if (parseInt(ctaLeftMargin.toString(), 10) === 0) return 0;
      if (parseInt(ctaLeftMargin.toString(), 10) === 10000) return "auto";
      return `[${parseInt(ctaLeftMargin.toString(), 10)}px]`;
    })()}`)
    setCtaRMargin(`right-${(() => {
      if (parseInt(ctaRightMargin.toString(), 10) === 0) return 0;
      if (parseInt(ctaRightMargin.toString(), 10) === 10000) return "auto";
      return `[${parseInt(ctaRightMargin.toString(), 10)}px]`;
    })()}`)

    // console.log("LOCALE | topNav 1", getLocales())
    setLocales(getLocales());
    // console.log("LOCALE | topNav 2", getLocales())
    window?.addEventListener("scroll", scrollPage);
    return () => {
      window?.removeEventListener("scroll", scrollPage);
    }
  }, [])

  useEffect(() => {
    // window?.addEventListener("scroll", scrollPage);
    if (tags) {
      (async () => {
        const content = await getTopNavData({ tags: tags || "baseplateLandingPage,menuGroup", preview: !!preview, id: id, locale: locale });
        setMenu(content?.items[0]);
      })();
    } else {
      setMenu(elData?.menu);
      // setContactFormData(elData?.contactFormData);
      // setDownloadFormData(elData?.downloadFormData);
    }
  }, [preview, id, locale, tags])

  useEffect(() => {
    if (!closeMenuEventName) return;

    const handleCloseMenu = (e: Event) => {
      setToggle(false);
    };

    window.addEventListener(closeMenuEventName, handleCloseMenu);

    return () => {
      window.removeEventListener(closeMenuEventName, handleCloseMenu);
    };
  }, [closeMenuEventName]);

  // const toggleMenu = () => {
  //   setToggle(!toggle)
  // }

  const toggleMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!pageScrolled) {
      setPageScrolled(!toggle);
    }
    if (toggle) {
      scrollPage();
      collapseEvent && e?.target?.dispatchEvent(new Event(collapseEvent, { bubbles: true }))

    }
    else {
      expandEvent && e?.target?.dispatchEvent(new Event(expandEvent, { bubbles: true }))
    }

    sendEventToGTM({
      event: "Top Nav | Burgger menu Clicked",
      component: "Top Nav",
      componentId: elData?.content?.id || id || "topNav",
      pagePath: window.location.pathname,
    })
    setToggle(!toggle);
  };

  const handleContactModalOpen = () => {
    sendEventToGTM({ event: "Top Nav | contact_us_modal", value: "visible" });
    setToggle(false);
    window.dispatchEvent(new CustomEvent('openContactUsModal', { detail: { submits } }));
    setSubmits(submits + 1);
  };

  // const handleContactModalOpen = () => {
  //   setModalType("contactForm");
  //   setShowModal(true);
  // }

  const handleModalClose = () => {
    setShowModal(false);
  }

  const BurgerMenuIcon = ({ className }: T_KeyValue) => {
    return (
      <div className={classNames(className, `navbar-toggler1 menu__icon cursor-pointer ${toggle ? "active" : "inactive"} mt-1`)}
        onClick={(e) => toggleMenu(e)}>
        <div className="bg-interactive-hamburger" />
        <div className="bg-interactive-hamburger" />
        <div className="bg-interactive-hamburger" />
      </div>
    )
  }

  const MenuList = ({ menuAlignment = menu?.content?.menuAlignment?.toLowerCase() || "right", pages = menu?.content?.pages || [], className = "!flex-col" }: T_KeyValue) => {
    return (
      <div className={classNames(`menu-list-container`, `w-full`)}>
        {/* <pre>{JSON.stringify(pages, null, 2)}</pre> */}
        <MenuItemList
          className={`flex ${className} items-${isMd ? menuAlignment === "left" ? "start" : (menuAlignment === "right" ? "end" : "center") : "center"}`}
          data={pages && filterMenuItems({ pages: (pages || []), filterArray: ['modalButton', 'linkButton'], notExists: true })}
          parentIndex={0}
          action={() => {
            setToggle(false);
          }}
        />
      </div>
    )
  }

  const filterMenuItems = ({ pages = [], filterArray = [], notExists = false }: { pages: any, filterArray: string[], notExists?: boolean }) => {
    return Array.isArray(pages) && pages
      ?.filter((p: any) => {
        return (
          notExists ? (
            !filterArray?.includes(p?.content?.contentType) && !filterArray?.includes(p?.fields?.contentType)
          ) : (
            filterArray?.includes(p?.content?.contentType) || filterArray?.includes(p?.fields?.contentType)
          ) // checking contentType in filterArray
          // return !filterArray?.includes(p?.content?.contentType) || !filterArray?.includes(p?.fields?.contentType) // checking contentType in filterArray
        )
      }) || [];

  }

  const bottomStickyCTAsJSX = (children: ReactNode) => {
    return (
      <div
        data-slot="bottomStickyCTAs"
        className={classNames(
          "fixed flex",
          menu?.content?.className,
          "w-full", "z-[109]",
          ctaAlign === "left"
            ? "justify-start"
            : ctaAlign === "center"
              ? "justify-center"
              : "justify-end"
        )}
        style={{
          // paddingLeft: ctaAlign === "left" ? `${(ctaLeftMargin === 10000 ? "auto" : ctaLeftMargin) || 0}px` : 0,
          // paddingRight: ctaAlign === "right" ? `${(ctaRightMargin === 10000 ? "auto" : ctaRightMargin) || 0}px` : 0,
          paddingLeft: `${ctaLeftMargin || 0}px`,
          paddingRight: `${ctaRightMargin || 0}px`,
          bottom: `${ctaBottomMargin || 0}px`
        }}
      >
        {children}
      </div>
    );
  }

  const CallToAction = ({ pages = menu?.content?.pages || [], buttonsBackgroundColor = "", buttonsBorderColor = "", localeList = [], className = "", buttonsClassName = "" }: T_KeyValue) => {
    return (
      <div className={classNames("nav-action-buttons", ctaWidth)}>
        {/* {pages && pages.filter((p: any) => ('modalButton' === p?.content?.contentType || 'linkButton' === p?.content?.contentType))?.map((btn: any, index: number) => { */}
        {filterMenuItems({ pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'] })?.map((btn: any, index: number) => {
          const button = { ...JSON.parse(JSON.stringify(btn)) }
          if (!button?.content) { button["content"] = button?.fields }

          if (button?.content?.contentType === 'modalButton' || button?.fields?.contentType === 'modalButton') {
            return (
              <Button
                key={(button?.content?.linkText || button?.fields?.linkText) || "cta-" + index}
                id="contact-us-button"
                size="m"
                variant="primary"
                action={handleContactModalOpen}
                cssClass={classNames(
                  "w-max text-navLink nav-button bg-button-nav-link-bg mix-blend-mode !border-1 border-base-border",
                  buttonsClassName
                )}
                style={{ backgroundColor: buttonsBackgroundColor, borderColor: (!isSm ? "#FFE500" : buttonsBorderColor) }}
              >
                {button.content.linkText || button?.fields?.linkText}
              </Button>
            )
          } else {
            return (
              <CustomLink
                key={button.content.linkText || button?.fields?.linkText}
                target={button.content.openInNewWindow ? "_blank" : "_self"}
                href={(button?.content?.externalLink || button?.fields?.externalLink) || (button?.content?.internalLink || button?.fields?.internalLink) || "/"}
                cssClass="w-full"
              >
                <Button
                  key={button.content.linkText || button?.fields?.linkText}
                  id="contact-us-button"
                  size="m"
                  variant="primary"
                  action={handleContactModalOpen}
                  cssClass={classNames(
                    "w-max text-navLink nav-button bg-button-nav-link-bg mix-blend-mode !border-1 border-base-border",
                    buttonsClassName
                  )}
                  style={{ backgroundColor: buttonsBackgroundColor, borderColor: (!isSm ? "#FFE500" : buttonsBorderColor) }}
                >
                  {button.content.linkText || button?.fields?.linkText}
                </Button>
              </CustomLink>
            )
          }
        })}
      </div>
    )
  }

  const visibleNavCta = () => {
    if (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'top navigation bar' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'top navigation bar') {
      return true;
    } else if (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'top navigation bar' && menu?.content?.ctaPositionOnMobile?.toLowerCase() !== 'top navigation bar') {
      return !isMd && isSm;
    } else if (menu?.content?.ctaPositionOnTab?.toLowerCase() !== 'top navigation bar' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'top navigation bar') {
      return isMd || !isSm;
    } else {
      return isMd;
    }

    // if(menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen') {
    //   return isMd;
    // } else if(menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() !== 'bottom of the screen') {
    //   return isMd || !isSm;
    // } else if(menu?.content?.ctaPositionOnTab?.toLowerCase() !== 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen') {
    //   return isSm;
    // } else {
    //   return true;
    // }
  }

  const visibleExpandedCta = () => {
    if (!["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnTab?.toLowerCase()) && !["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnMobile?.toLowerCase())) {
      return !isMd;
    } else if (!["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnTab?.toLowerCase()) && ["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnMobile?.toLowerCase())) {
      return !isMd && isSm;
    } else if (["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnTab?.toLowerCase()) && !["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnMobile?.toLowerCase())) {
      return !isSm;
    } else {
      return false;
    }
  }

  const visibleBottomCta = () => {
    if (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen') {
      return !isMd;
    } else if (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() !== 'bottom of the screen') {
      return !isMd && isSm;
    } else if (menu?.content?.ctaPositionOnTab?.toLowerCase() !== 'bottom of the screen' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen') {
      return !isSm;
    } else {
      return false;
    }
  }

  const ThreeContainerJSX = () => {
    return (
      <Stack
        fluid
        column={!isMd}
        className={classNames("w-full", {
          "top-menu-container-1 max-w-[1440px] m-auto": isMd,
          "max-w-full !flex-row": !isMd
        })}
        style={{
          flexFlow: "row" //menu?.content?.backUrl ? "row" : "column"
        }}
        vAlignContent="center"
      >
        {/* <pre>{JSON.stringify(menu?.content?.pages?.filter((p: any) => 'modalButton' !== p?.content?.contentType), null, 2)}</pre> */}
        {/* ================================================== */}
        {/* =================== Left segment ================= */}
        {/* ================================================== */}

        <div className={classNames('flex', 'justify-start', 'items-center', 'w-full')}>
          {/* ------------------- Back button ------------------ */}
          {menu?.content?.backUrl &&
            <CustomLink href={menu?.content?.backUrl === "/" ? localizedHomeHref : (menu?.content?.backUrl || "/")}>
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="35" viewBox="0 0 41 40" fill="none" role="img" aria-label="Back icon">
                <path d="M10.5 19.9008C10.5062 19.4612 10.6849 19.0418 10.997 18.7341L24.2515 5.40077C24.5684 5.12772 24.9761 4.98504 25.393 5.00124C25.81 5.01744 26.2056 5.19133 26.5006 5.48815C26.7957 5.78498 26.9686 6.18289 26.9847 6.60235C27.0008 7.02181 26.8589 7.43194 26.5875 7.75078L14.5094 19.9008L26.5875 32.0674C26.7609 32.2168 26.9019 32.4007 27.0013 32.6075C27.1008 32.8143 27.1566 33.0395 27.1654 33.269C27.1742 33.4985 27.1358 33.7274 27.0525 33.9413C26.9692 34.1552 26.8429 34.3494 26.6815 34.5118C26.52 34.6743 26.3269 34.8014 26.1143 34.8852C25.9016 34.969 25.6741 35.0077 25.4459 34.9988C25.2178 34.9899 24.9939 34.9337 24.7883 34.8337C24.5828 34.7336 24.4 34.5919 24.2515 34.4174L10.997 21.0841C10.8405 20.9298 10.716 20.7457 10.6307 20.5426C10.5453 20.3394 10.5009 20.1213 10.5 19.9008Z" fill="#474747" />
              </svg>
            </CustomLink>
          }

          {/* ------------------- Burger menu icon ------------------ */}
          {menu?.content?.burgerIconPosition?.toLowerCase() === "left" &&
            ((menu?.content?.enableBurgerMenu || menu?.content?.isAMegaMenu) && (
              <BurgerMenuIcon className="mr-2" />
            ))
          }
          {!customizeTopMenu ? (
            <>
              {/* ------------------- Logo ------------------ */}
              {menu?.content?.logoAlignment?.toLowerCase() === "left" &&
                <NavigationLogo src={menu?.content?.logo?.content?.file?.url} data={menu?.content?.logo?.content?.file} locale={locale} />
              }

              {/* ------------------- Menu list ------------------ */}
              {menu?.content?.pages && menu?.content?.pages?.length > 0 && (menu?.content?.isAMegaMenu ? (
                menu?.content?.menuPosition?.toLowerCase() === "left" && <MenuList />
              ) : (
                isMd && menu?.content?.menuAlignment?.toLowerCase() === "left" && <MenuList className="flex-row" />
              ))}
            </>
          ) : (
            !hideLeftSegment && <div className={classNames('w-full')}>{leftMenuSegment}</div>
          )}
        </div>


        {/* ==================================================== */}
        {/* =================== Center segment ================= */}
        {/* ==================================================== */}

        <div className={classNames('flex', 'w-full', `justify-${menu?.content?.logoAlignment?.toLowerCase() === 'left' ? 'start' : (menu?.content?.logoAlignment?.toLowerCase() === 'right' ? 'end' : 'center')}`, 'items-center')}>
          {!customizeTopMenu ? (
            <>
              {/* ------------------- Logo ------------------ */}
              {menu?.content?.logoAlignment?.toLowerCase() === "center" &&
                <NavigationLogo src={menu?.content?.logo?.content?.file?.url} data={menu?.content?.logo?.content?.file} locale={locale} />
              }
              {/* ------------------- Menu list ------------------ */}
              {menu?.content?.pages && menu?.content?.pages?.length > 0 && (menu?.content?.isAMegaMenu ? (
                menu?.content?.menuPosition?.toLowerCase() === "center" && <MenuList />
              ) : (
                isMd && menu?.content?.menuAlignment?.toLowerCase() === "center" && <MenuList className="flex-row" />
              ))}
            </>
          ) : (
            !hideCenterSegment && <div className={classNames('w-full')}>{centerMenuSegment}</div>
          )}
        </div>


        {/* =================================================== */}
        {/* =================== Right segment ================= */}
        {/* =================================================== */}

        {/* <div className={classNames('flex', 'w-full', `justify-${menu?.content?.logoAlignment?.toLowerCase() === 'left' ? 'start' : (menu?.content?.logoAlignment?.toLowerCase() === 'right' ? 'end' : 'center')}`, 'items-center')}> */}
        <div className={classNames('flex', 'w-full', 'justify-end', 'items-center', 'min-h-[30px]')}>
          {!customizeTopMenu ? (
            <>
              {/* ------------------- Logo ------------------ */}
              {menu?.content?.logoAlignment?.toLowerCase() === "right" &&
                <NavigationLogo src={menu?.content?.logo?.content?.file?.url} data={menu?.content?.logo?.content?.file} locale={locale} />
              }

              {/* ------------------- Menu list ------------------ */}
              {menu?.content?.isAMegaMenu ? (
                <>
                  {/* {menu?.content?.menuPosition?.toLowerCase() === "right" && <MenuList />} */}
                  {/* ------------------- Call to actions ------------------ */}
                  {visibleNavCta() &&
                    <div className={classNames('flex', menu?.content?.className, 'ml-2', 'justify-between')}>
                      {filterMenuItems({ pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'], notExists: false }) &&
                        <div className={classNames("nav-action-buttons w-full px-2")}>
                          <CallToAction className="ml-2" buttonsBackgroundColor={buttonsBackgroundColor} buttonsBorderColor={buttonsBorderColor} />
                        </div>
                      }
                    </div>
                  }
                </>
              ) : (
                <>
                  {isMd && menu?.content?.menuAlignment?.toLowerCase() === "right" && <MenuList className="flex-row" />}
                  {/* <pre style={{overflow: "scroll", height: "500px"}}>{JSON.stringify(menu?.content, null, 2)}</pre> */}

                  {/* ------------------- Call to actions ------------------ */}
                  {/* {(menu?.content?.enableBurgerMenu?.toLowerCase() === 'yes' && menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'top') &&  */}
                  {(visibleNavCta()) &&
                    <div className={classNames('flex', menu?.content?.className, 'ml-2', 'justify-between')}>
                      {menu?.content?.pages && filterMenuItems({ pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'], notExists: false }) &&
                        <div className={classNames("nav-action-buttons w-full px-2")}>
                          <CallToAction className="ml-2" buttonsBackgroundColor={buttonsBackgroundColor} buttonsBorderColor={buttonsBorderColor} />
                        </div>
                      }
                    </div>
                  }
                </>
              )}
            </>
          ) : (
            !hideRightSegment && <div className={classNames('w-full')}>{rightMenuSegment}</div>
          )}


          {/* ------------------- Locale ------------------ */}
          {(locales || [])?.length > 1 &&
            <LanguageChanger locale={locale} localeList={locales || []} />
          }

          {/* ------------------- Burger menu icon ------------------ */}
          {menu?.content?.burgerIconPosition?.toLowerCase() !== "left" &&
            ((menu?.content?.enableBurgerMenu || menu?.content?.isAMegaMenu) && (
              <BurgerMenuIcon className="ml-2" />
            ))
          }
        </div>
      </Stack>
    )
  }
  return (
    <>
      {/* <pre style={{padding: "100px"}}>
        --- {JSON.stringify(isInContentfulStudio, null, 2)} --- 
        {JSON.stringify(elData, null, 2)}
        {JSON.stringify(elData?.menu?.content?.pages[0]?.sys?.urn, null, 2)}
      </pre> */}
      <StyledNav
        data-testid="TopMenu"
        className={classNames(
          `transition ease-in-out delay-0 duration-300`,
          `navbar navbar-expand-lg navbar-light bg-interactive-top-nav bg-light z-[110] w-screen !items-start ${toggle ? " pb-3" : ""} ${menu?.content?.cssClass}`,
          toCamelCase(menu?.content?.internalName || ""),
          // (menu?.content?.varient === "Fixed on top" ? "nav-fixed" : (menu?.content?.varient === "Hide on scroll" ? (scrollDirection === "down" ? "bg-interactive-sticky-top-nav -top-20" : "bg-interactive-sticky-top-nav -top-0") : "bg-interactive-sticky-top-nav !fixed"))
          (() => {
            // const vrnt = isSm ? menu?.content?.mobileVarient : (isMd ? menu?.content?.tabVarient : menu?.content?.varient);
            // switch (!isSm ? menu?.content?.mobileVarient : (!isMd ? menu?.content?.tabVarient : menu?.content?.varient)) {
            switch (varient) {
              case 'Sticky on top':
                // if(toggle) {
                //   return 'bg-interactive-sticky-top-nav navbar-bg-on-scroll nav-sticky !fixed';
                // } else 
                if (pageScrolled || (toggle)) {
                  return `bg-interactive-sticky-top-nav navbar-bg-on-scroll nav-sticky ${!isInContentfulStudio && '!fixed'}`;
                } else {
                  return `bg-interactive-sticky-top-nav top-[0] ${!isInContentfulStudio && '!fixed'}`;
                }
              case 'Hide on scroll':
                if (scrollDirection === "down") {
                  if (toggle) {
                    return 'bg-interactive-sticky-top-nav !fixed navbar-bg-on-scroll top-0';
                  } else {
                    return 'bg-interactive-sticky-top-nav -top-20';
                  }
                } else {
                  if (toggle) {
                    return 'bg-interactive-sticky-top-nav !fixed navbar-bg-on-scroll top-0';
                  }
                  return `${pageScrolled ? "navbar-bg-on-scroll" : ""} ${pageScrolled && !toggle ? (!isInContentfulStudio && '!fixed') : "!sticky"} top-0`;
                }
              case 'Static':
                return `navbar-bg-on-scroll`;
              default:
                return `${!isInContentfulStudio && 'nav-fixed'} navbar-bg-on-scroll`;
            }
          })()
        )}
        style={
          {
            minHeight: 50,
            ...menu?.content?.customStyles,
            ...(menu?.content?.paddingTop || menu?.content?.paddingTop === 0 ? { paddingTop: menu?.content?.paddingTop + menu?.content?.paddingUnit || 'px' } : {}),
            ...(menu?.content?.paddingRight || menu?.content?.paddingRight === 0 ? { paddingRight: menu?.content?.paddingRight + menu?.content?.paddingUnit || 'px' } : {}),
            ...(menu?.content?.paddingBottom || menu?.content?.paddingBottom === 0 ? { paddingBottom: menu?.content?.paddingBottom + menu?.content?.paddingUnit || 'px' } : {}),
            ...(menu?.content?.paddingLeft || menu?.content?.paddingLeft === 0 ? { paddingLeft: menu?.content?.paddingLeft + menu?.content?.paddingUnit || 'px' } : {}),
            ...(menu?.content?.backgroundColor ? { backgroundColor: menu?.content?.backgroundColor } : {})
          }
        }
      >
        {/* <pre>Content ----- {JSON.stringify(menu?.content, null, 2)}</pre> */}
        {/* <pre>Enable burger menu ---- {JSON.stringify(menu.content.enableBurgerMenu)}</pre> */}
        <div className={classNames("w-full", "h-[auto]")}>
          {/* ----------------- Three contaier top navbar code start from here ------------------- */}
          {/* {ThreeContainerJSX()} */}
          {/* ----------------- Code ends here --------------- */}
          {/*  customizeTopMenu is true when you want a flxible approach to display the contentful child elements from the slot
           customizeTopMenu is false when you want to display the elements according to the design tokens selected */}
          {!customizeTopMenu ?
            <ThreeContainerJSX></ThreeContainerJSX> :
            <Stack
              fluid
              column={!isMd}
              className={classNames("w-full", {
                "top-menu-container-1 max-w-[1440px] m-auto": isMd,
                "max-w-full !flex-row": !isMd
              })}
              style={{
                // flexFlow: "row", 
                "alignItems": "center"//menu?.content?.backUrl ? "row" : "column"
              }}
              vAlignContent="center"
            >
              {/* <div><pre>{JSON.stringify(locales, null, 2)}</pre></div> */}
              <div className={classNames('')}>
                {/* ------------------- Back button ------------------ */}
                {menu?.content?.backUrl &&
                  <CustomLink href={menu?.content?.backUrl === "/" ? localizedHomeHref : (menu?.content?.backUrl || "/")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="35" viewBox="0 0 41 40" fill="none">
                      <path d="M10.5 19.9008C10.5062 19.4612 10.6849 19.0418 10.997 18.7341L24.2515 5.40077C24.5684 5.12772 24.9761 4.98504 25.393 5.00124C25.81 5.01744 26.2056 5.19133 26.5006 5.48815C26.7957 5.78498 26.9686 6.18289 26.9847 6.60235C27.0008 7.02181 26.8589 7.43194 26.5875 7.75078L14.5094 19.9008L26.5875 32.0674C26.7609 32.2168 26.9019 32.4007 27.0013 32.6075C27.1008 32.8143 27.1566 33.0395 27.1654 33.269C27.1742 33.4985 27.1358 33.7274 27.0525 33.9413C26.9692 34.1552 26.8429 34.3494 26.6815 34.5118C26.52 34.6743 26.3269 34.8014 26.1143 34.8852C25.9016 34.969 25.6741 35.0077 25.4459 34.9988C25.2178 34.9899 24.9939 34.9337 24.7883 34.8337C24.5828 34.7336 24.4 34.5919 24.2515 34.4174L10.997 21.0841C10.8405 20.9298 10.716 20.7457 10.6307 20.5426C10.5453 20.3394 10.5009 20.1213 10.5 19.9008Z" fill="#474747" />
                    </svg>
                  </CustomLink>
                }

                {/* ------------------- Burger menu icon ------------------ */}
                {menu?.content?.burgerIconPosition?.toLowerCase() === "left" &&
                  ((menu?.content?.enableBurgerMenu || menu?.content?.isAMegaMenu) && (
                    <BurgerMenuIcon className="mr-2" />
                  ))
                }
              </div>
              <div className={classNames('w-full')}>{topMenuContainer}</div>
              {((locales || [])?.length > 1 || menu?.content?.burgerIconPosition?.toLowerCase() !== "left") &&
                <div className={classNames(
                  'min-w-[40px] min-h-[30px]'
                )} >
                  {/* ------------------- Locale ------------------ */}
                  {/* {getDefaultLocale()} --- {locale} */}
                  {(locales || [])?.length > 1 &&
                    <LanguageChanger locale={locale} localeList={locales || []} />
                  }

                  {/* ------------------- Burger menu icon ------------------ */}
                  {menu?.content?.burgerIconPosition?.toLowerCase() !== "left" &&
                    ((menu?.content?.enableBurgerMenu || menu?.content?.isAMegaMenu) && (
                      <BurgerMenuIcon className="ml-2" />
                    ))
                  }
                </div>}
            </Stack>
          }

          {/* =============================================================================================== */}
          {/* ---------------------------------------- Expended menu list ----------------------------------- */}
          {/* =============================================================================================== */}

          {menu?.content?.isAMegaMenu &&
            <div
              className={"w-full " + (() => {
                return !isInContentfulStudio ? classNames(
                  `transition ease-in-out delay-0 duration-300 overflow-auto ${megaMenuMaxHeight}`,
                  `flex navbar-collapse justify-between pt-1 ${toggle ? "h-[auto]" : "collapse"}`,
                  menu?.content?.isAMegaMenu ? "!flex-row" : "!flex-col"
                ) : "block"
              })()}>
              {/* customize top menu is true to allow flexibility and false when you want attached menu entries */}
              {/* custtomizeTopmenu when true will display the Contentful slot components otherwise will display the entries from the menu group */}
              {customizeTopMenu ? expandedMenu : <MenuList />}
            </div>
          }

          {/* <Stack 
            className={"w-full " + (()=>{
              return !isInContentfulStudio ? classNames(
                `flex navbar-collapse justify-between pt-1 ${toggle ? "h-[auto]" : "collapse"}`,
                menu?.content?.isAMegaMenu ? "!flex-row" : "!flex-col"
              ) : "block"
            })()}
          >
            <></>
            {expandedMenu} */}
          {/* ------------------- Menu list ------------------ */}
          {/* <MenuList /> */}

          {/* ------------------- Call to actions ------------------ */}
          {/* {(menu?.content?.enableBurgerMenu?.toLowerCase() !== 'yes') && 
              <div className={classNames('flex', menu?.content?.className, {'ml-2': menu?.content?.isAMegaMenu}, 'justify-between')}>
                {menu?.content?.pages && filterMenuItems({pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'], notExists: false }) &&
                  <div className={classNames("nav-action-buttons w-full px-2")}>
                    <CallToAction className="ml-2" buttonsBackgroundColor={buttonsBackgroundColor} buttonsBorderColor={buttonsBorderColor} />
                  </div>
                }
              </div>
            } */}
          {/* </Stack> */}
        </div>
      </StyledNav>


      {/* =============================================================================================== */}
      {/* -------------------------- Sticky CTA buttons on bottom for small device ---------------------- */}
      {/* =============================================================================================== */}

      {!customizeTopMenu ? (
        <>
          {/* {ctaBottomMargin} */}
          {menu?.content?.isAMegaMenu ? (
            <>
              {/* {visibleBottomCta() &&
                <div className={classNames(
                  'flex', 
                  menu?.content?.className, 
                  ctaBMargin,
                  ctaLMargin,
                  ctaRMargin, 
                  // 'bottom-[15px]',
                  'fixed left-0 flex flex-row !justify-center w-full z-[109] px-[30px]', 
                  'justify-between'
                  )}>
                  {menu?.content?.pages && filterMenuItems({ pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'], notExists: false }) &&
                    <div className={classNames("nav-action-buttons w-full px-2")}>
                      <CallToAction className="ml-2" buttonsBackgroundColor={buttonsBackgroundColor} buttonsBorderColor={buttonsBorderColor} buttonsClassName={"!w-full"} />
                    </div>
                  }
                </div>
              } */}
              {visibleBottomCta() && (
                <div
                  data-slot="bottomStickyCTAs"
                  className={classNames(
                    "fixed flex",
                    menu?.content?.className,
                    "w-full", "z-[109]",
                    ctaAlign === "left"
                      ? "justify-start"
                      : ctaAlign === "center"
                        ? "justify-center"
                        : "justify-end"
                  )}
                  style={{
                    // paddingLeft: ctaAlign === "left" ? `${(ctaLeftMargin === 10000 ? "auto" : ctaLeftMargin) || 0}px` : 0,
                    // paddingRight: ctaAlign === "right" ? `${(ctaRightMargin === 10000 ? "auto" : ctaRightMargin) || 0}px` : 0,
                    paddingLeft: `${ctaLeftMargin || 0}px`,
                    paddingRight: `${ctaRightMargin || 0}px`,
                    bottom: `${ctaBottomMargin || 0}px`
                  }}
                >
                  <>
                    <CallToAction
                      buttonsBackgroundColor={buttonsBackgroundColor}
                      buttonsBorderColor={buttonsBorderColor}
                      buttonsClassName={`!${ctaWidth}`}
                    />
                  </>
                </div>
              )}
            </>
          ) : (
            <>
              {/* <div style={{color: "black", paddingTop: 120}}>
                <pre>{JSON.stringify(((!["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnMobile?.toLowerCase()) ? '!isSm' : (!["top navigation bar", "bottom of the screen"].includes(menu?.content?.ctaPositionOnTab?.toLowerCase()) ? '!isMd' : '!isLg'))))}</pre>
                <pre>{JSON.stringify(((menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen' ? (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' ? 'isLg' : '!isSm') : 'false')))}</pre>
                <pre>isSm --- {JSON.stringify(isSm)}</pre>
                <pre>isMd --- {JSON.stringify(isMd)}</pre>
                <pre>isLg --- {JSON.stringify(isLg)}</pre>
                <pre>Tab ---- {menu?.content?.ctaPositionOnTab?.toLowerCase()}</pre>
                <pre>Mobile ---- {menu?.content?.ctaPositionOnMobile?.toLowerCase()}</pre>
                <pre> visibleExpandedCta ---- {JSON.stringify(visibleExpandedCta())}</pre>
                <pre> visibleBottomCta ---- {JSON.stringify(visibleBottomCta())}</pre>
                <pre> visibleNavCta ---- {JSON.stringify(visibleNavCta())}</pre>
              </div> */}
              {/* {((menu?.content?.ctaPositionOnMobile?.toLowerCase() === 'bottom of the screen' ? (menu?.content?.ctaPositionOnTab?.toLowerCase() === 'bottom of the screen' ? !isMd : !isSm) : false)) &&  */}

              {/* {visibleBottomCta() &&
                <div className={classNames(
                  'flex', 
                  menu?.content?.className, 
                  ctaBMargin,
                  ctaLMargin,
                  ctaRMargin, 
                  'fixed left-0 bottom-[15px] flex flex-row !justify-center w-full z-[109] px-[30px]', 'justify-between')}>
                  {menu?.content?.pages && filterMenuItems({ pages: (menu?.content?.pages || []), filterArray: ['modalButton', 'linkButton'], notExists: false }) &&
                    <div className={classNames("nav-action-buttons w-full px-2")}>
                      <CallToAction 
                        className="ml-2" 
                        buttonsBackgroundColor={buttonsBackgroundColor} 
                        buttonsBorderColor={buttonsBorderColor} 
                        buttonsClassName={`!${ctaWidth}`} />
                    </div>
                  }
                </div>
              } */}

              {visibleBottomCta() && (
                <div
                  data-slot="bottomStickyCTAs"
                  className={classNames(
                    "fixed flex",
                    menu?.content?.className,
                    "w-full", "z-[109]",
                    ctaAlign === "left"
                      ? "justify-start"
                      : ctaAlign === "center"
                        ? "justify-center"
                        : "justify-end"
                  )}
                  style={{
                    // paddingLeft: ctaAlign === "left" ? `${(ctaLeftMargin === 10000 ? "auto" : ctaLeftMargin) || 0}px` : 0,
                    // paddingRight: ctaAlign === "right" ? `${(ctaRightMargin === 10000 ? "auto" : ctaRightMargin) || 0}px` : 0,
                    paddingLeft: `${ctaLeftMargin || 0}px`,
                    paddingRight: `${ctaRightMargin || 0}px`,
                    bottom: `${ctaBottomMargin || 0}px`
                  }}
                >
                  <CallToAction
                    className="ml-2"
                    buttonsBackgroundColor={buttonsBackgroundColor}
                    buttonsBorderColor={buttonsBorderColor}
                    buttonsClassName={`!${ctaWidth}`}
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        // <div className={classNames(menu?.content?.className, 'fixed bottom-[15px] w-full z-[109] px-[30px]')}>
        <div
          data-slot="bottomStickyCTAs"
          className={classNames(
            "fixed flex",
            menu?.content?.className,
            "w-full", "z-[109]",
            ctaAlign === "left"
              ? "justify-start"
              : ctaAlign === "center"
                ? "justify-center"
                : "justify-end"
          )}
          style={{
            // paddingLeft: ctaAlign === "left" ? `${(ctaLeftMargin === 10000 ? "auto" : ctaLeftMargin) || 0}px` : 0,
            // paddingRight: ctaAlign === "right" ? `${(ctaRightMargin === 10000 ? "auto" : ctaRightMargin) || 0}px` : 0,
            paddingLeft: `${ctaLeftMargin || 0}px`,
            paddingRight: `${ctaRightMargin || 0}px`,
            bottom: `${ctaBottomMargin || 0}px`
          }}
        >
          {sameAsTopMenuCTAs ? rightMenuSegment : bottomStickyCTAs}
          {/* {bottomStickyCTAs} */}
          {/* {rightMenuSegment} */}
        </div>
      )}

      {/* =================================== Modal ================================== */}
      {(showModal && modalType) && (
        <Modal data={modalType === 'contactForm' ? contactFormData : downloadFormData} handleModalClose={handleModalClose} />
      )}
    </>
  );
};

export default TopMenu;
